import { useRef, useEffect, useCallback, useState } from 'react';
import { image } from 'cademy-shared-web';
import styles from './ImageGallery.module.css';
import { Img } from '..';
import AliceCarousel from 'react-alice-carousel';
import 'react-alice-carousel/lib/alice-carousel.css';
import { CarouselThumbnails } from './CarouselThumbnails';
import { useMediaQuery } from 'react-responsive';

export const ImageGallery = ({ onClick, onChange, img_urls = [], fallback_image = {} }) => {
    const [sliderIndex, setSliderIndex] = useState(0);
    const isMobile = useMediaQuery({ query: '(max-width: 860px)' });
    const itemsPerPage = 1;
    const carouselRef = useRef();

    const slidePrev = () => {
        setSliderIndex(sliderIndex - 1);
    };
    const slideNext = () => {
        setSliderIndex(sliderIndex + 1);
    };

    const syncActiveIndex = ({ item }) => {
        setSliderIndex(item);
    };

    const handleUserKeyPress = useCallback(
        (event) => {
            const { keyCode } = event;
            if (keyCode === 37 && sliderIndex !== 0) {
                slidePrev();
            }
            if (keyCode === 39 && sliderIndex < img_urls.length - 1) {
                slideNext();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sliderIndex, img_urls.length]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleUserKeyPress);
        return () => {
            window.removeEventListener('keydown', handleUserKeyPress);
        };
    }, [handleUserKeyPress]);

    useEffect(() => {
        if (onChange) onChange(sliderIndex);
    }, [onChange, sliderIndex]);

    const handlePictureClick = (img_url) => {
        if (isMobile) {
            const goToImg = sliderIndex === img_urls.length - 1 ? 0 : sliderIndex + 1;
            return setSliderIndex(goToImg);
        }
        if (onClick) return onClick(img_url);
    };

    const getItems = () => {
        let items = img_urls.map((img_url) => {
            const baseUrl = image.getBaseUrl(img_url);
            return (
                <div key={img_url}>
                    <picture
                        onDragStart={(e) => e.preventDefault()}
                        onClick={() => handlePictureClick(img_url)}
                    >
                        <Img
                            src={img_url}
                            alt=""
                            className={[styles.single_course_img, 'item'].join(' ')}
                        />
                        <source
                            type="image/webp"
                            media="(max-width: 450px)"
                            srcSet={`${baseUrl}w400.webp 1x, ${baseUrl}w800.webp 2x`}
                        />
                        <source
                            type="image/webp"
                            media="(max-width: 650px)"
                            srcSet={`${baseUrl}w600.webp 1x, ${baseUrl}w1600.webp 2x`}
                        />
                        <source
                            type="image/webp"
                            media="(max-width: 860px)"
                            srcSet={`${baseUrl}w800.webp 1x, ${baseUrl}w1600.webp 2x`}
                        />
                        <source
                            type="image/webp"
                            media="(max-width: 1049px)"
                            srcSet={`${baseUrl}w600.webp 1x, ${baseUrl}w1600.webp 2x`}
                        />
                        <source
                            type="image/webp"
                            media="(min-width: 1050px)"
                            srcSet={`${baseUrl}w800.webp 1x, ${baseUrl}w1600.webp 2x`}
                        />
                        <source srcSet={image.getFallbackUrl(img_url)} />
                    </picture>
                </div>
            );
        });

        return items;
    };

    if (img_urls && img_urls.length === 1) {
        const baseUrl = image.getBaseUrl(img_urls[0]);
        return (
            <picture onClick={() => handlePictureClick(image.getFallbackUrl(img_urls[0]))}>
                <Img src={img_urls[0]} alt="" className={styles.single_course_img} />
                <source
                    type="image/webp"
                    media="(max-width: 450px)"
                    srcSet={`${baseUrl}w400.webp 1x, ${baseUrl}w800.webp 2x`}
                />
                <source
                    type="image/webp"
                    media="(max-width: 650px)"
                    srcSet={`${baseUrl}w600.webp 1x, ${baseUrl}w1600.webp 2x`}
                />
                <source
                    type="image/webp"
                    media="(max-width: 860px)"
                    srcSet={`${baseUrl}w800.webp 1x, ${baseUrl}w1600.webp 2x`}
                />
                <source
                    type="image/webp"
                    media="(max-width: 1049px)"
                    srcSet={`${baseUrl}w600.webp 1x, ${baseUrl}w1600.webp 2x`}
                />
                <source
                    type="image/webp"
                    media="(min-width: 1050px)"
                    srcSet={`${baseUrl}w800.webp 1x, ${baseUrl}w1600.webp 2x`}
                />
                <source srcSet={image.getFallbackUrl(img_urls[0])} />
            </picture>
        );
    }

    if (img_urls && img_urls.length > 1) {
        return (
            <div className={styles.course_img_carousel}>
                <AliceCarousel
                    mouseTracking
                    items={getItems()}
                    disableDotsControls
                    disableButtonsControls
                    activeIndex={sliderIndex}
                    animationDuration={400}
                    animationType="slide"
                    onSlideChanged={syncActiveIndex}
                    ref={carouselRef}
                />
                {sliderIndex === 0 || isMobile ? null : (
                    <button
                        onClick={slidePrev}
                        className="new-carousel-nav-btn prev"
                        style={{ top: 'calc(42%)' }}
                    ></button>
                )}
                {sliderIndex + itemsPerPage >= img_urls.length || isMobile ? null : (
                    <button
                        onClick={slideNext}
                        className="new-carousel-nav-btn next"
                        style={{ top: 'calc(42%)' }}
                    ></button>
                )}
                <CarouselThumbnails
                    activeIndex={sliderIndex}
                    img_urls={img_urls}
                    onImgClick={(i) => setSliderIndex(i)}
                />
            </div>
        );
    }

    if (fallback_image.url) {
        return (
            <>
                <img
                    src={fallback_image.url}
                    alt="Automatically generated course depiction"
                    className={styles.single_course_img}
                />
                <span className={styles.auto_img_source_notice}>
                    Please note, this image was automatically generated based on the course title
                    and may not reflect the actual experience. Stock photo by{' '}
                    {fallback_image.author} on Unsplash.{' '}
                </span>
            </>
        );
    }

    return (
        <img
            src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-1.2.1&q=80&fm=jpg&crop=entropy&cs=tinysrgb&w=1080&fit=max&ixid=eyJhcHBfaWQiOjExODk3Mn0"
            alt="Automatically generated course depiction"
            className={styles.single_course_img}
        />
    );
};
